import { useEffect, useRef } from "react";

// A modal sheet, standing in for SwiftUI's .sheet presentation.
//
// Built on <dialog> so focus trapping, Esc-to-dismiss, and inertness of the
// page behind come from the platform rather than hand-rolled listeners.
//
// On phones it is anchored to the bottom and sized between the app's medium
// and large detents; from the `sm` breakpoint up it becomes a centred panel,
// matching the app switching to .large in a regular size class.

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Names the dialog for assistive tech. */
  label: string;
  /**
   * How tall the sheet sits, standing in for the app's presentation detents.
   * "medium" is roughly half the screen and grows with its content; "large"
   * takes essentially the whole screen.
   */
  detent?: "medium" | "large";
  children: React.ReactNode;
}

// dvh rather than vh so mobile browser chrome does not push the bottom of the
// sheet off screen.
const DETENT = {
  medium: "min-h-[60dvh] max-h-[90dvh] sm:min-h-0 sm:max-h-[85vh]",
  large: "h-[92dvh] max-h-[92dvh] sm:h-[85vh] sm:max-h-[85vh]",
} as const;

export default function Sheet(props: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (props.open) {
      dialog.removeAttribute("data-closing");
      if (!dialog.open) dialog.showModal();
      return;
    }

    if (!dialog.open) return;

    // Play the exit before actually closing. Calling close() straight away
    // would drop the dialog out of the top layer immediately in any browser
    // that cannot transition `overlay` (Safari), skipping the animation.
    dialog.setAttribute("data-closing", "");

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      dialog.removeAttribute("data-closing");
      dialog.close();
    };

    // Only the dialog's own transition counts; children may have their own.
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === dialog) finish();
    };

    dialog.addEventListener("transitionend", handleTransitionEnd);
    // Safety net for reduced motion, or if no transition runs at all.
    const timer = window.setTimeout(finish, 400);

    return () => {
      dialog.removeEventListener("transitionend", handleTransitionEnd);
      window.clearTimeout(timer);
    };
  }, [props.open]);

  // While the sheet is up, the sheet scrolls rather than the page behind it.
  // showModal() makes the page inert but does not lock its scroll.
  useEffect(() => {
    if (!props.open) return;

    const { body, documentElement: html } = document;
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
    };

    // Hiding the scrollbar would otherwise let the page behind shift sideways.
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    // Lock <html> only. The root element's overflow applies to the viewport,
    // so this stops the page scrolling without creating a new scroll container.
    // Do not also lock <body>: `overflow: hidden` there makes <body> a scroll
    // container, and every `position: sticky` element (like the service detail
    // toolbar) re-anchors to it — its scrollTop is 0, so they jump back to
    // their unstuck position while a sheet is open.
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.paddingRight = previous.bodyPaddingRight;
    };
  }, [props.open]);

  const { onClose } = props;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Esc would otherwise close the dialog outright, skipping the exit. Cancel
    // the native dismissal and let React drive it like any other close.
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    // Backstop for anything that closes the dialog without going through
    // React, so `open` cannot get stuck true.
    dialog.addEventListener("cancel", handleCancel);
    dialog.addEventListener("close", onClose);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      dialog.removeEventListener("close", onClose);
    };
  }, [onClose]);

  return <dialog
    ref={ref}
    aria-label={props.label}
    // A click landing on the dialog itself is the backdrop: the panel inside
    // covers the whole element, so any other click hits a child.
    onClick={(event) => {
      if (event.target === ref.current) props.onClose();
    }}
    className={`sheet m-0 mt-auto mb-0 flex w-full max-w-none flex-col overflow-hidden rounded-t-[20px] bg-surface p-0 sm:m-auto sm:w-md sm:rounded-[20px] ${DETENT[props.detent ?? "medium"]}`}
  >
    {props.children}
  </dialog>
}
